<template>
  <div class="header" :style="bgc">
    <b-col>
      <b-row class="topheader-container">
        <h5 class="application-sub-heading">
          <p
            v-if="applicationSubHeading && applicationSubHeading.length > 0"
            class="applicationSubHeading"
          >
            {{ applicationSubHeading }}
          </p>
        </h5>
        <div class="topheader">
          <div id="logo" class="top-header-left-flex">
            <a :href="config_WebCaatAuthWebUrl" class="d-flex">
              <img src="@/assets/images/mms_logo_white.svg" alt="MMS Logo" />
            </a>
            <h1 class="env-text">{{ environment }}</h1>
          </div>
          <div class="top-header-middle">
            <div class="text-white webtools-horizontal">
              <!-- Loop through apps and render AppNavItem for each -->
              <app-nav-item
                v-for="(app, index) in apps"
                :key="app.id"
                :app-id="app.id"
                :app-data="app"
                :is-current-app="currentApp === app.id"
                :app-index="index"
                :progress-offset="progressOffset"
                :app-link="
                  app.configLinkKey ? getAppLink(app.configLinkKey) : '#'
                "
                :icon-src="app.iconSrc"
                :header-background-color="config_BackgroundColor"
                :show-info-icon="app.showInfoIcon"
                @start-hover="startHover"
                @stop-hover="stopHover"
              />
            </div>
          </div>
          <div class="top-header-right-flex contextmenu-container">
            <div class="webtools-dropdown-container">
              <b-dropdown
                id="dropdown-2"
                text="More tools"
                class="m-2 webtools-dropdown"
              >
                <!-- Render all apps except Portalen above the divider -->
                <template v-for="app in apps.filter(a => a.id !== 'portal')">
                  <app-dropdown-item
                    :key="app.id + '-dropdown'"
                    :is-current-app="currentApp === app.id"
                    :icon-src="
                      currentApp === app.id ? app.iconSrc : app.iconSrcBlue
                    "
                    :label="app.title"
                    :app-link="
                      app.configLinkKey ? getAppLink(app.configLinkKey) : '#'
                    "
                  />
                </template>

                <b-dropdown-divider></b-dropdown-divider>

                <!-- Render only Portalen below the divider -->
                <template v-for="app in apps.filter(a => a.id === 'portal')">
                  <app-dropdown-item
                    :key="app.id + '-dropdown'"
                    :is-current-app="currentApp === app.id"
                    :icon-src="
                      currentApp === app.id ? app.iconSrc : app.iconSrcBlue
                    "
                    :label="app.title"
                    :app-link="
                      app.configLinkKey ? getAppLink(app.configLinkKey) : '#'
                    "
                  />
                </template>
              </b-dropdown>
            </div>
            <b-dropdown
              id="dropdown-1"
              split
              :text="$store.getters.getUser"
              class="contextmenu-login"
            >
              <b-dropdown-item @click="logout">Log out</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-row>
    </b-col>
  </div>
</template>

<script>
import globalConfig from "@/mixins/globalConfig";
import AppNavItem from "./AppNavItem.vue";
import AppDropdownItem from "./AppDropdownItem.vue";
import appsConfig from "./appsConfig.js";

export default {
  name: "PageHeader",
  components: {
    AppNavItem,
    AppDropdownItem
  },
  props: {
    currentApp: {
      type: String,
      validator: function(value) {
        return [
          "broadcast",
          "online-video",
          "cross-platform",
          "video-in-home",
          "portal"
        ].includes(value);
      }
    }
  },
  mixins: [globalConfig],
  data() {
    return {
      apps: appsConfig, // Use the imported app configuration
      applicationHeading: "",
      applicationSubHeading: "",

      // Hover info card settings
      infoCardPreDelay: 400, // Delay before showing progress (ms)
      infoCardDelay: 800, // Total time for progress circle to fill (ms)
      infoCardPostDelay: 200, // Delay after progress completion before showing popup (ms)

      // Hover tracking variables
      hoveredIndex: null, // Which item is being hovered
      infoVisibleIndex: null, // Which item shows the progress indicator
      hoverProgress: 0, // Progress from 0 to 100
      // Timers
      hoverDelayTimer: null, // Timer for pre-delay
      progressInterval: null, // Interval for progress updates
      postDelayTimer: null // Timer for post-delay
    };
  },
  async mounted() {
    await this.loadGlobalConfigFromFile();
    this.applicationHeading = this.config_ApplicationHeading;

    // Set the appropriate application sub-heading based on the current app
    this.applicationSubHeading =
      this.currentApp === "video-in-home"
        ? this.config_ApplicationSubHeadingVideoInHome
        : this.config_ApplicationSubHeading;
  },
  computed: {
    // Calculate offset for SVG circle progress
    progressOffset() {
      const circumference = 2 * Math.PI * 10; // 2πr where r=10
      const offset = circumference - (this.hoverProgress / 100) * circumference;
      return offset;
    },
    bgc() {
      return {
        "background-color": this.config_BackgroundColor
      };
    },
    environment() {
      const heading = (this.applicationHeading || "").toLowerCase();
      if (heading.includes(" dev")) {
        return "DEV";
      } else if (heading.includes(" test")) {
        return "TEST";
      } else if (heading.includes(" local")) {
        return "LOCAL";
      }
      return "";
    },
    // Create a default empty app data object to prevent null errors
    defaultAppData() {
      return {
        title: "",
        capabilities: []
      };
    }
  },
  methods: {
    // Methods for hover info functionality
    startHover(index) {
      // If already hovering on this item, do nothing
      if (this.hoveredIndex === index) return;

      // Reset everything first
      this.stopHover();
      this.hoveredIndex = index;

      // Wait infoCardPreDelay before showing the progress indicator
      this.hoverDelayTimer = setTimeout(() => {
        this.apps[index].showInfoIcon = true;
        this.infoVisibleIndex = index;
        this.startProgress(index);
      }, this.infoCardPreDelay);
    },

    startProgress(index) {
      this.hoverProgress = 0;

      // Calculate progress steps
      const increments = 20; // 20 steps of 5% each
      const stepTime = this.infoCardDelay / increments;

      this.progressInterval = setInterval(() => {
        if (this.hoverProgress < 100) {
          this.hoverProgress += 5;
        } else {
          clearInterval(this.progressInterval);
          this.progressInterval = null;

          // After progress is complete, wait infoCardPostDelay before showing popup
          this.postDelayTimer = setTimeout(() => {
            // Only show popup if user is still hovering this same item
            if (this.hoveredIndex === index) {
              this.popupIndex = index;
            }
          }, this.infoCardPostDelay);
          setTimeout(() => (this.apps[index].showInfoIcon = false), 100);
          this.apps[index].popupVisible = true;
        }
      }, stepTime);
    },

    stopHover() {
      // Clear all timers and intervals
      clearTimeout(this.hoverDelayTimer);
      clearInterval(this.progressInterval);
      clearTimeout(this.postDelayTimer);

      // Reset all states
      this.hoveredIndex = null;
      this.infoVisibleIndex = null;
      this.popupIndex = null;
      this.hoverDelayTimer = null;
      this.progressInterval = null;
      this.postDelayTimer = null;
      this.hoverProgress = 0;
      this.apps.forEach(app => (app.popupVisible = false));
      this.apps.forEach(app => (app.showInfoIcon = false));
    },
    async logout() {
      await this.$store.dispatch("logout");
      window.location.href = this.config_WebCaatAuthWebUrl;
    },
    toastMessage(message, closeButton) {
      if (closeButton) {
        const h = this.$createElement;

        const vNodesMsg = h("b", { class: ["text-center", "mb-0"] }, [
          "Problem fetching default date from the server. The service will not function correctly.",
          h(
            "b-button",
            {
              class: ["float-right", "btn-sm", "mr-3"],
              on: {
                click: () => this.$bvToast.hide("toast")
              }
            },
            "Close"
          )
        ]);

        this.$bvToast.toast([vNodesMsg], {
          id: "toast",
          title: "Warning",
          variant: "danger",
          solid: true,
          noCloseButton: true,
          autoHideDelay: 300000
        });
      } else {
        this.$bvToast.toast(message, {
          title: "Warning",
          variant: "warning",
          solid: true,
          noCloseButton: false
        });
      }
    },
    getAppLink(key) {
      return this[key] !== undefined ? this[key] : "#";
    }
  }
};
</script>

<style scoped>
>>> .header {
  background-color: #337ab7;
  height: 70px;
  position: relative; /* Added to position the popup card relative to header */
}
>>> .topheader-container {
  height: 70px;
  padding: 0 20px;
  align-content: center;
}
>>> .topheader {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  height: 100%;
}
>>> .application-sub-heading {
  position: absolute;
  top: 52px;
}
>>> .top-header-left-flex {
  width: 25%;
  display: flex;
  justify-content: flex-start;
  align-content: center;
}
>>> .top-header-right-flex {
  width: 25%;
  display: flex;
  justify-content: flex-end;
}
>>> .top-header-middle {
  width: 50%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
>>> .top-header-left-flex img {
  width: 8em;
}
>>> .applicationSubHeading {
  font-size: small;
  color: white !important;
  -webkit-text-stroke: 0.04em red;
  font-weight: bold;
}
>>> .v-context a {
  color: #495057;
}
>>> .env-text {
  color: white;
  padding: 10px 0 0 0;
  margin-left: 1rem;
}

/* Dropdown and Context Menu Styles */
>>> .contextmenu-login > button:first-child:hover {
  border-color: #6c757d;
  cursor: default;
}
>>> .top-header-right-flex button {
  background-color: inherit !important;
}
>>> #dropdown-1 {
  max-height: 40px;
  align-self: center;
}
>>> .webtools-dropdown-container {
  border: none !important;
  margin-top: 7px !important;
  padding: 0;
}
>>> .webtools-dropdown button {
  border: none !important;
  border-color: transparent !important;
  padding-left: 1rem;
}
>>> .webtools-dropdown ul {
  font-size: 0.8rem;
}

/* Dropdown Item Styles */
>>> .webtool-icon-dropdown {
  width: 24px;
  height: 24px;
  margin-right: 0.8rem;
  margin-bottom: 0.15rem;
  object-fit: contain;
}

/* Navigation Container */
>>> #dropdown-2 {
  display: none; /* Hidden by default on large screens */
}
>>> .webtools-horizontal {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  height: 100%;
  width: 100%;
  max-width: 1100px;
}

/* Below 1100px: Switch to dropdown menu */
@media (max-width: 1099px) {
  #dropdown-2 {
    display: block;
  }

  .webtools-horizontal {
    display: none;
  }
}
</style>
